import { useMemo } from "react";
import { AnalyticsChartCard } from "../ChartComponents";

function mergeAnalyticsByDate(metrics) {
    const map = {};

    metrics.forEach(({ key, source, valueKey }) => {
        (source || []).forEach(item => {
            if (!map[item.date]) {
                map[item.date] = { date: item.date };
            }
            map[item.date][key] = item[valueKey] || 0;
        });
    });

    return Object.values(map).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
    );
}

function formatTokenCount(value) {
    if (value >= 1_000_000) {
        const m = value / 1_000_000;
        return m % 1 === 0 ? `${m}M` : `${m.toFixed(1)}M`;
    }
    if (value >= 1_000) {
        const k = value / 1_000;
        return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
    }
    return String(value);
}

export default function TokenUsageAnalytics({ tokenUsage, loading }) {
    const tokenChart = useMemo(() => {
        const sourceData = tokenUsage?.token_usage_datewise || [];

        return {
            chartData: mergeAnalyticsByDate([
                {
                    key: "input_tokens",
                    source: sourceData,
                    valueKey: "total_input_tokens",
                },
                {
                    key: "output_tokens",
                    source: sourceData,
                    valueKey: "total_output_tokens",
                },
            ]),
            lines: [
                {
                    dataKey: "input_tokens",
                    stroke: "#6366F1",
                    name: "Input Tokens",
                },
                {
                    dataKey: "output_tokens",
                    stroke: "#22C55E",
                    name: "Output Tokens",
                },
            ],
        };
    }, [tokenUsage]);

    const topUsers = useMemo(() => {
        return (tokenUsage?.top_users || []).slice(0, 10);
    }, [tokenUsage]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-[#ECEEF4] bg-white p-6">
                <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                    Loading token usage analytics…
                </div>
            </div>
        );
    }

    return (
        <div className="w-full min-w-0">
            <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
                <AnalyticsChartCard
                    variant="design"
                    title="Token Usage Over Time"
                    subtitle=""
                    {...tokenChart}
                />

                <div className="min-w-0 w-full overflow-hidden rounded-2xl border border-[#ECEEF4] bg-white">
                    <div className="px-5 pb-4 pt-5 sm:px-6">
                        <h2 className="text-base font-bold tracking-tight text-[#14122B]">Top 10 Users by Token Usage</h2>
                    </div>

                    {topUsers.length > 0 ? (
                        <div className="table-scroll overflow-auto max-h-[300px]">
                            <table className="w-full min-w-[360px] border-collapse text-sm">
                                <thead className="sticky top-0 z-10">
                                    <tr className="border-y border-[#ECEEF4] bg-[#FAFAFD] text-[11px] font-semibold uppercase tracking-wide text-[#9AA1B2]">
                                        <th className="w-14 py-2.5 pl-5 pr-2 text-left sm:pl-6">#</th>
                                        <th className="py-2.5 px-2 text-left">Name</th>
                                        <th className="py-2.5 pl-2 pr-5 text-right sm:pr-6">Total Usage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topUsers.map((user, index) => (
                                        <tr
                                            key={user.user_id}
                                            className="border-b border-[#F1F2F7] last:border-b-0 hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="py-3 pl-5 pr-2 align-middle text-sm font-semibold text-[#7C3AED] sm:pl-6">{index + 1}</td>
                                            <td className="max-w-0 py-3 px-2 align-middle">
                                                <div className="truncate text-sm font-bold text-[#14122B]" title={`${user.first_name} ${user.last_name}`}>
                                                    {user.first_name} {user.last_name}
                                                </div>
                                                <div className="truncate text-xs text-[#9AA1B2]" title={user.email}>{user.email}</div>
                                            </td>
                                            <td className="whitespace-nowrap py-3 pl-2 pr-5 text-right align-middle text-sm font-extrabold text-[#14122B] sm:pr-6">
                                                {formatTokenCount(user.total_tokens || 0)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="mx-5 mb-5 flex items-center justify-center h-[200px] text-sm text-[#9AA1B2] border-2 border-dashed border-[#ECEEF4] rounded-xl sm:mx-6 sm:mb-6">
                            No user token usage data available
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
